/**
This component is a wrapper that controls the rendering of the video component so that it does not load until the user chooses to click and play the video when the video is embedded.
It also prevents the video from loading if the video is paid content that the user has not purchased
**/
import React, { useEffect, useState } from "react";
import axios from "axios";
import Video from "../Video";
import { BASE_PATH } from "../../constants/awsBaseUrl";
import Loader from "../../icons/Loader";

import "./Video.css"

// Enum
const VIDEO_STATUS = Object.freeze({
  LOADING: Symbol("loading"),
  UNAVAILABLE: Symbol("unavailable"), // For when video is temporarily unavailable (e.g. the video is transcoding)
  PAYMENT_REQUIRED: Symbol("payment required"),
  ERROR: Symbol("error"),
  READY: Symbol("ready")
})

const VideoPlayWrapper = (props) => {
  let {
    video,
    embed,
    type,
    url,
    pub_user,
    file,
    viewerId
  } = props

  const [videoStatus, setVideoStatus] = useState(VIDEO_STATUS.LOADING)
  const [shouldPlay, setShouldPlay] = useState(!embed) // Controls if the video should be rendered.
  const [price, setPrice] = useState(0)
  const [videoPaidFor, setVideoPaidFor] = useState(false)
  const [customThumb, setCustomThumb] = React.useState(false)
  const [videoURL, setVideoURL] = React.useState("")
  const [format, setFormat] = React.useState("HLS")

  // default attempt is to get video in HLS format; that is why type is initialized to HLS
  const getVideo = async () => {
    try {
      if(file?._id && !file?.transcoded) {
        setVideoStatus(VIDEO_STATUS.UNAVAILABLE)
        return;
      }
      let res = await axios.post(url + "/upload/get", {
        file: video,
        type: format,
        user: pub_user ? pub_user._id : "",
      })
      if (res.data.response) {
        if (res.data.response.payload.data === "/") {
          /*
            fetch details if a pay-to-view video has been bought.
            along with the url to the mp4 version of that video.
            Only run if a user has been logged in
          */
          if (viewerId) {
            //If no url is returned in the response retrieve mp4 version from the stripe api
            res = await axios.post(
              "https://do2v45e3qi.execute-api.us-west-2.amazonaws.com/production/get-video-from-aws",
              {
                videoId: video,
                viewerId: viewerId,
              }
            )
            setVideoURL(res.data.videoMP4.split("?")[0])
            setVideoPaidFor(res.data.videoHasBeenPaidFor)
            if(res.data.videoHasBeenPaidFor) {
              setVideoStatus(VIDEO_STATUS.READY)
            } else {
              setVideoStatus(VIDEO_STATUS.PAYMENT_REQUIRED)
            }
          } else {
            setPrice(res.data.response.payload.price)
            setVideoStatus(VIDEO_STATUS.PAYMENT_REQUIRED)
          }
        } else {
          setVideoURL(res.data.response.payload.data.split("?")[0])
          setPrice(0)
          setVideoStatus(VIDEO_STATUS.READY)
        }
      } else {
        setVideoStatus(VIDEO_STATUS.UNAVAILABLE)
      }
    } catch (err) {
      console.error(err);
      if (format === "HLS") {
        console.warn("HLS Failed. Trying MP4...");
        setFormat("MP4")
      } else {
        setVideoStatus(VIDEO_STATUS.ERROR)
      }
    }
  };

  const getLive = async () => {
    try {
      setVideoStatus(VIDEO_STATUS.LOADING)
      setVideoPaidFor(true) // Live being paid for is handled separately
      try {
        let tres = await axios.head(`${BASE_PATH}/outputs/${encodeURIComponent(video)}/Default/Thumbnails/out_003.png?refresh=${new Date().getMonth()}${new Date().getDate()}${new Date().getFullYear()}`)
        if(tres.status === 200) {
          setCustomThumb(true)
        }
      } catch (err) {
        if(!err.response || err.response.status !== "404") {
          console.error("Error getting custom thumbnail:")
          console.error(err)
        }
      }
      let res = await axios.post(`${url}/upload/getLive`, {live: video})
      if (res.data.response.success) {
        let {live} = res.data.response.payload
        live = live.trim();
        setVideoURL(live)
        setVideoStatus(VIDEO_STATUS.READY)
      } else {
        setVideoStatus(VIDEO_STATUS.UNAVAILABLE)
      }
    } catch (err) {
      console.error(err)
      setVideoStatus(VIDEO_STATUS.ERROR)
    }
  }

  useEffect(() => {
    if(type === "live") {
      getLive()
    } else {
      getVideo()
    }
  }, [video, pub_user, viewerId, type, format])

  let customThumbnailUrl=`url('${BASE_PATH}/outputs/${encodeURIComponent(video)}/Default/Thumbnails/out_003.png?refresh=${new Date().getMonth()}${new Date().getDate()}${new Date().getFullYear()}')`
  let og = type === "live" ?
    `${BASE_PATH}/live/thumbs/${encodeURIComponent(video)}/thumb.jpg?refresh=${new Date().getMonth()}${new Date().getDate()}${new Date().getFullYear()}` :
    `${BASE_PATH}/outputs/${video}/Default/Thumbnails/out_003.png?refresh=${new Date().getMonth()}${new Date().getDate()}${new Date().getFullYear()}`
  let thumbnailURL = customThumb?customThumbnailUrl:og

  let className = "videoPlayerContainer"
  let secondaryAudioTracks = []
  if(type !== "live" && file?.sapAudioTracks && file.sapAudioTracks.length) {
    secondaryAudioTracks = file.sapAudioTracks.filter((track) => (track.file || track.trackNumber)).map((track) => {
      if(track.file) {
        return {
          ...track,
          url: `${BASE_PATH}/outputs/${video}/Default/SAP/${track.file}`
        }
      } else {
        return {
          ...track,
          url: `${BASE_PATH}/outputs/${video}/Default/SAP/ad.sap.track${track.trackNumber}.wav`
        }
      }
    })
  }

  let content;
  switch(videoStatus) {
    case VIDEO_STATUS.READY:
      content = (<Video {...props} videoURL={videoURL} thumbnailURL={thumbnailURL} secondaryAudioTracks={secondaryAudioTracks} format={format}/>)
      break;
    case VIDEO_STATUS.PAYMENT_REQUIRED:
      className += " noVideo"
      content = (<div className="video-thumbnail"
          style={{
            backgroundImage: `url('${thumbnailURL}'`,
            display: "flex"
          }}>
        <div>
          <i className="fas fa-dollar-sign"
            onClick={() => {
              props.setItemBeingBought('payToView');
              if (price > 0 && videoPaidFor) {
                // prompt user to make an account
                if (pub_user._id === "") {
                  props.register();
                }
              } else {
                setVideoStatus(VIDEO_STATUS.READY);
              }
            }}
            ></i>
        </div>
      </div>)
      break;
    case VIDEO_STATUS.LOADING:
      className += " noVideo"
      content = (<div className="video-thumbnail"
          style={{
            backgroundImage: `url('${thumbnailURL}'`,
            display: "flex"
          }}>
        <div>
          <Loader/>
        </div>
      </div>)
      break;
    case VIDEO_STATUS.UNAVAILABLE:
      className += " noVideo"
      content = (<div className="video-thumbnail"
          style={{
            backgroundImage: `url('${thumbnailURL}'`,
            display: "flex"
          }}>
        <div className="video-message">
          <i className="fas fa-warning"/>
          <div style={{color: "white", fontWeight: "bold"}}>
            This video is not yet available. Please check back again later!
          </div>
        </div>
      </div>)
      break;
    case VIDEO_STATUS.ERROR:
    default:
      className += " noVideo"
      content = (<div className="video-thumbnail"
          style={{
            backgroundImage: `url('${thumbnailURL}'`,
            display: "flex"
          }}>
        <div className="video-message">
          <i className="fas fa-remove"/>
          <div style={{color: "white", fontWeight: "bold"}}>
            There was an error loading the video.
            Please refresh the page or try again later.
          </div>
        </div>
      </div>)
      break;
  }

  return (
    <div className={`${className} ${embed?"embedded":"not-embedded"}`}>
      {content}
    </div>
  )
}

export default VideoPlayWrapper
